import { httpsCallable } from "firebase/functions";
import { collection, getDocs, query, where } from "firebase/firestore";
import { functions, db } from "../Firebase.js";
import { normalizeVimplayGames, VimplayGame } from "../../models/VimplayGame.tsx";

export const VIMPLAY_GAMES_ENABLED = true;

export function canAccessAdminCasino(user?: { userType?: string | null } | null): boolean {
  return user?.userType === "masterAgent" || user?.userType === "subAgent";
}

export function canAccessVimplayGames(user?: { casinoEnabled?: boolean } | null): boolean {
  if (!VIMPLAY_GAMES_ENABLED) return false;
  return user?.casinoEnabled === true;
}

export async function getVimplayGames(): Promise<VimplayGame[]> {
  const getGames = httpsCallable(functions, "getVimplayGames");
  const { data } = await getGames();
  return normalizeVimplayGames(data);
}

export interface VimplayPlayerBonus {
  uniqueCode: string;
  gameId: number;
  gameName: string;
  freeBetAmount: number;
  freeBetRemaining: number;
  expiredAt: number | null;
  status: string;
  createdAtMs: number;
}

function millis(value: unknown): number {
  if (value && typeof value === "object" && "toMillis" in value &&
    typeof (value as { toMillis: () => number }).toMillis === "function") {
    return (value as { toMillis: () => number }).toMillis();
  }
  return typeof value === "number" ? value : 0;
}

export async function getPlayerVimplayBonuses(
  playerId: string,
): Promise<VimplayPlayerBonus[]> {
  const bonusQuery = query(
    collection(db, "vimplayBonuses"),
    where("playerId", "==", playerId),
  );
  const snap = await getDocs(bonusQuery);
  const now = Date.now();
  return snap.docs
    .map((bonusDoc) => {
      const data = bonusDoc.data();
      return {
        uniqueCode: String(data.uniqueCode ?? bonusDoc.id),
        gameId: Number(data.gameId),
        gameName: String(data.gameName ?? ""),
        freeBetAmount: Number(data.freeBetAmount ?? 0),
        freeBetRemaining: Number(data.freeBetRemaining ?? 0),
        expiredAt: data.expiredAt == null ? null : Number(data.expiredAt),
        status: String(data.status ?? ""),
        createdAtMs: millis(data.createdAt),
      };
    })
    .filter((bonus) =>
      bonus.status === "created" &&
      bonus.freeBetRemaining > 0 &&
      (bonus.expiredAt == null || bonus.expiredAt > now),
    )
    .sort((a, b) => a.createdAtMs - b.createdAtMs);
}

export async function getVimplayLaunchUrl(gameId: number): Promise<string> {
  const launch = httpsCallable<{ gameId: number }, { gameUrl?: string }>(
    functions,
    "getVimplayLaunchUrl",
  );
  const { data } = await launch({ gameId });
  const gameUrl = data?.gameUrl?.trim();
  if (!gameUrl) {
    throw new Error("Vimplay launch URL is missing");
  }
  return gameUrl;
}
